/**
 * Multicall3 read helper for SportsFuture.
 *
 * Use for batched view calls (admin application lists, DID KYC, role checks).
 * Never route writes (submit / review / register / sign-in) through this —
 * Multicall3 becomes msg.sender and those txs must stay wallet-direct.
 */

import { Contract, providers, utils } from 'ethers';
import {
  APERTUM_CHAIN_ID,
  APERTUM_RPC,
  MULTICALL3_ADDRESS,
} from './apertumChain';

export { MULTICALL3_ADDRESS };

const MULTICALL3_ABI = [
  'function aggregate3(tuple(address target, bool allowFailure, bytes callData)[] calls) payable returns (tuple(bool success, bytes returnData)[] returnData)',
];

const DEFAULT_CHUNK_SIZE = 80;

export interface MulticallCall {
  address: string;
  abi: readonly string[] | string[];
  functionName: string;
  args?: unknown[];
}

export interface MulticallResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface MulticallOptions {
  provider?: providers.Provider;
  chunkSize?: number;
}

export function isMulticallBatchLevelFailure(err: unknown): boolean {
  const error = err as { code?: string; message?: string };
  const message = error.message ?? '';
  return (
    error.code === 'CALL_EXCEPTION' ||
    message.includes('missing revert data') ||
    message.includes('reverted without a reason')
  );
}

export function apertumMulticallProvider(): providers.JsonRpcProvider {
  return new providers.StaticJsonRpcProvider(APERTUM_RPC, {
    name: 'apertum',
    chainId: APERTUM_CHAIN_ID,
  });
}

function encodeCall(call: MulticallCall): { target: string; callData: string } {
  const iface = new utils.Interface([...call.abi]);
  return {
    target: call.address,
    callData: iface.encodeFunctionData(call.functionName, call.args ?? []),
  };
}

function decodeResult(call: MulticallCall, data: string): unknown {
  const iface = new utils.Interface([...call.abi]);
  const decoded = iface.decodeFunctionResult(call.functionName, data);
  return decoded.length === 1 ? decoded[0] : decoded;
}

async function executeChunk(
  multicall: Contract,
  chunk: MulticallCall[]
): Promise<MulticallResult[]> {
  const encoded = chunk.map(encodeCall).map(({ target, callData }) => ({
    target,
    allowFailure: true,
    callData,
  }));
  const raw: Array<{ success: boolean; returnData: string }> =
    await multicall.callStatic.aggregate3(encoded);
  return raw.map((row, idx) => {
    if (!row.success) {
      return { success: false, error: row.returnData || 'Call reverted' };
    }
    try {
      return { success: true, data: decodeResult(chunk[idx], row.returnData) };
    } catch (err) {
      return {
        success: false,
        error: `Decode error: ${err instanceof Error ? err.message : 'unknown'}`,
      };
    }
  });
}

async function executeChunkWithFallback(
  multicall: Contract,
  chunk: MulticallCall[]
): Promise<MulticallResult[]> {
  try {
    return await executeChunk(multicall, chunk);
  } catch (err) {
    if (!isMulticallBatchLevelFailure(err)) throw err;
    if (chunk.length <= 1) {
      return [
        {
          success: false,
          error: err instanceof Error ? err.message : 'RPC batch call failed',
        },
      ];
    }
    const mid = Math.ceil(chunk.length / 2);
    const [left, right] = await Promise.all([
      executeChunkWithFallback(multicall, chunk.slice(0, mid)),
      executeChunkWithFallback(multicall, chunk.slice(mid)),
    ]);
    return [...left, ...right];
  }
}

/** Batched view calls via Apertum Multicall3 aggregate3. */
export async function multicallRead(
  calls: MulticallCall[],
  options: MulticallOptions = {}
): Promise<MulticallResult[]> {
  if (calls.length === 0) return [];
  const chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const provider = options.provider ?? apertumMulticallProvider();
  const multicall = new Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, provider);
  const out: MulticallResult[] = [];
  for (let i = 0; i < calls.length; i += chunkSize) {
    const chunk = calls.slice(i, i + chunkSize);
    out.push(...(await executeChunkWithFallback(multicall, chunk)));
  }
  return out;
}
